import {
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  ChangeEvent,
  RenderInputComponentProps,
  RenderSuggestionsContainerParams,
} from 'react-autosuggest';
import Autosuggest from 'react-autosuggest';

import { AutosuggestWrapper } from '#/components/Autocomplete/Autocomplete.styled';
import type {
  InputAffixVariant,
  InputStyleVariant,
} from '#/components/Input/Input.types';
import { useDebouncedFn } from '#/hooks/useDebouncedFn';
import { useIsFirstRenderRef } from '#/hooks/useIsFirstRenderRef';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { REMOTE_KEYWORDS } from '#/utils/constants/search';
import { useGMapsErrored } from '#/zustand-stores/gMapsErrorStore';
import {
  fetchGoogleMapsPlaceDetails,
  loadGoogleMapsLibraries,
  useGoogleMapsAutocomplete,
} from '#/zustand-stores/googleMapsLibrariesStore';

import { geocoderResultToSearchLocation } from './geocoderResultToSearchLocation';
import { getRemoteOption } from './getRemoteOption';
import { LocationInputSuggestion } from './LocationInputSuggestion';
import type { LocationSuggestion } from './LocationSuggestion';
import { StyledInput } from './StyledInput';
import { SuggestionsContainer } from './SuggestionsContainer';

const MINIMUM_QUERY_LENGTH = 2;

type Props = {
  onChange: (searchLocation: SearchLocation | undefined) => void;
  onClear?: () => void;
  hasSuccess?: boolean;
  qaId?: string;
  placeholder?: string;
  required?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  showLocationIcon?: boolean;
  showClearButton?: boolean;
  className?: string;
  handlePlaceDetails?: (place: google.maps.GeocoderResult) => void;
  addressTypes?: string[];
  includeCity?: boolean;
  includeFull?: boolean;
  disabled?: boolean;
  hasRemoteOptions?: boolean;
  highlightFirstSuggestion?: boolean;
  includedRegionCodes?: string[];
  affixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
  locationText: string | undefined | null;
  id: string;
};

function shouldRenderLocationSuggestions(value: string) {
  return Boolean(value.trim().length >= MINIMUM_QUERY_LENGTH);
}

function getAutosuggestListboxId(autosuggestId: string) {
  return `react-autowhatever-${autosuggestId}`;
}

function getValidActiveDescendant({
  activeDescendant,
  autosuggestId,
  hasOptionsPopup,
  suggestionsLength,
}: {
  activeDescendant: string | undefined;
  autosuggestId: string;
  hasOptionsPopup: boolean;
  suggestionsLength: number;
}) {
  if (!activeDescendant || !hasOptionsPopup) return undefined;

  const optionIdPrefix = `react-autowhatever-${autosuggestId}--item-`;
  if (!activeDescendant.startsWith(optionIdPrefix)) return undefined;

  const itemIndex = Number(activeDescendant.slice(optionIdPrefix.length));

  return Number.isInteger(itemIndex) &&
    itemIndex >= 0 &&
    itemIndex < suggestionsLength
    ? activeDescendant
    : undefined;
}

export function LocationInput({
  hasSuccess,
  onChange,
  onClear,
  placeholder,
  required,
  inputRef: customInputRef,
  showLocationIcon = false,
  showClearButton,
  className,
  addressTypes,
  includeCity,
  includeFull,
  disabled,
  highlightFirstSuggestion = true,
  includedRegionCodes,
  hasRemoteOptions,
  locationText,
  handlePlaceDetails: handlePlaceDetailsProp,
  qaId,
  id,
  affixVariant,
  styleVariant,
}: Props) {
  const { i18n, locale } = useI18n();

  const [defaultKeyword] = REMOTE_KEYWORDS[locale];
  const defaultInputRef = useRef<HTMLInputElement>(null);
  const inputRef = customInputRef ?? defaultInputRef;
  const autosuggestId = `${id}-autosuggest`;
  const listboxId = getAutosuggestListboxId(autosuggestId);

  const [suggestionsTuple, setSuggestionsTuple] = useState<{
    input: string;
    suggestions: LocationSuggestion[];
  } | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [suggestionsAreCollapsed, setSuggestionsAreCollapsed] = useState(true);

  const { gMapsErrored } = useGMapsErrored();

  const [locationInputValue, setLocationInputValue] = useKeyedState(
    locationText ?? '',
    { key: locationText ?? '' },
  );

  const { googleMapsAutocomplete, abortGoogleMapsAutocomplete } =
    useGoogleMapsAutocomplete();

  const debouncedLoadSuggestions = useDebouncedFn((input: string) => {
    void (async () => {
      const result = await googleMapsAutocomplete({
        input,
        includedRegionCodes,
        addressTypes,
      });

      if (result.type === 'aborted') return;

      const options = result.suggestions;

      const remoteOptions: (LocationSuggestion | undefined)[] = [];

      if (hasRemoteOptions) {
        remoteOptions.push(
          getRemoteOption({ locale, value: input, defaultKeyword }),
        );
      }

      setSuggestionsTuple({
        input,
        suggestions: [...remoteOptions, ...(options || [])].filter(Boolean),
      });
    })();
  }, 300);

  const clearLocation = useCallback(() => {
    setLocationInputValue('');
    onClear?.();
    onChange(undefined);
    setSuggestionsTuple(null);
    setSuggestionsAreCollapsed(true);
    abortGoogleMapsAutocomplete();
  }, [onChange, onClear, setLocationInputValue, abortGoogleMapsAutocomplete]);

  const onLocationInputValueChange = (
    _: FormEvent<unknown>,
    { newValue }: ChangeEvent,
  ) => {
    if (newValue === '') clearLocation();
    setSuggestionsAreCollapsed(!shouldRenderLocationSuggestions(newValue));
    setLocationInputValue(newValue);
  };

  const onBlur = () => {
    setIsInputFocused(false);
    setSuggestionsAreCollapsed(true);

    if (locationInputValue && locationInputValue !== locationText) {
      clearLocation();
      setSuggestionsTuple(null);
      abortGoogleMapsAutocomplete();
    }
  };

  const handlePlaceDetails = useCallback(
    async (place: google.maps.GeocoderResult) => {
      // Use the formatted address, in rare cases where a suggested place isn't
      // formatted identically to its geocoded version.
      setLocationInputValue(place.formatted_address);

      if (handlePlaceDetailsProp) {
        handlePlaceDetailsProp(place);
        return;
      }

      await geocoderResultToSearchLocation(place, {
        includeCity,
        includeFull,
      }).then(onChange);
    },
    [
      setLocationInputValue,
      handlePlaceDetailsProp,
      includeCity,
      includeFull,
      onChange,
    ],
  );

  const onSuggestionSelected = useCallback(
    async (
      _: FormEvent<HTMLInputElement>,
      { suggestion }: { suggestion: LocationSuggestion },
    ) => {
      abortGoogleMapsAutocomplete();
      setSuggestionsAreCollapsed(true);

      if (suggestion.isRemoteOption) {
        onChange({
          text: null,
          stateCode: null,
          countryCode: null,
          boundingBoxN: null,
          boundingBoxW: null,
          boundingBoxS: null,
          boundingBoxE: null,
          geoType: null,
          latitude: null,
          longitude: null,
          isRemote: true,
          timezone: null,
        });

        return;
      }

      const placeId = suggestion.placePrediction?.placeId;
      if (placeId) {
        const placeDetails = await fetchGoogleMapsPlaceDetails({
          placeId,
          language: locale,
        });
        if (placeDetails) void handlePlaceDetails(placeDetails);
      }
    },
    [locale, handlePlaceDetails, onChange, abortGoogleMapsAutocomplete],
  );

  const onSuggestionsFetchRequested = useCallback(
    ({ value }: { value: string }) => {
      setSuggestionsAreCollapsed(!shouldRenderLocationSuggestions(value));
      debouncedLoadSuggestions(value);
    },
    [debouncedLoadSuggestions],
  );

  const renderSuggestion = useCallback(
    (suggestion: LocationSuggestion) => (
      <LocationInputSuggestion suggestion={suggestion} />
    ),
    [],
  );

  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestionsAreCollapsed(true);
    setSuggestionsTuple({ input: locationInputValue, suggestions: [] });
    abortGoogleMapsAutocomplete();
  }, [locationInputValue, abortGoogleMapsAutocomplete]);

  const { isFirstRenderRef } = useIsFirstRenderRef();
  useEffect(() => {
    const inputValue = inputRef.current?.value ?? '';
    if (inputValue) setLocationInputValue(inputValue);

    if (
      isFirstRenderRef.current &&
      inputRef.current === document.activeElement &&
      inputValue &&
      !suggestionsTuple
    ) {
      // load when focused
      setIsInputFocused(true);
      setSuggestionsAreCollapsed(!shouldRenderLocationSuggestions(inputValue));
      debouncedLoadSuggestions(inputValue);
    }
  }, [
    isFirstRenderRef,
    suggestionsTuple,
    debouncedLoadSuggestions,
    inputRef,
    setLocationInputValue,
  ]);

  const getShowLoading = useCallback(
    (query: string) => {
      const inputHasMinimumLength = query.length >= MINIMUM_QUERY_LENGTH;

      const previousSuggestionsNotDisplaying =
        !suggestionsTuple ||
        suggestionsTuple.input.length < MINIMUM_QUERY_LENGTH ||
        !suggestionsTuple.suggestions;

      const noResultsForQuery = suggestionsTuple?.input !== query;

      const notCurrentLocation = locationText !== query;

      return (
        isInputFocused &&
        !suggestionsAreCollapsed &&
        inputHasMinimumLength &&
        previousSuggestionsNotDisplaying &&
        noResultsForQuery &&
        notCurrentLocation
      );
    },
    [isInputFocused, locationText, suggestionsAreCollapsed, suggestionsTuple],
  );

  const renderSuggestionsContainer = ({
    containerProps,
    children,
    query,
  }: RenderSuggestionsContainerParams) => {
    // Skip rendering container during SSR
    if (!inputRef.current) return null;

    const showLoading = getShowLoading(query);

    if (!showLoading && !children) return null;

    return (
      <SuggestionsContainer
        containerProps={containerProps}
        listboxId={listboxId}
        loading={showLoading}
      >
        {children}
      </SuggestionsContainer>
    );
  };

  const renderInputComponent = useCallback(
    ({
      ref,
      autoComplete,
      className: inputClassName,
      disabled: inputDisabled,
      onBlur: inputOnBlur,
      onChange: inputOnChange,
      onFocus,
      onKeyDown,
      onKeyPress,
      title,
      value,
      ...renderInputComponentProps
    }: RenderInputComponentProps) => {
      const showLoading = getShowLoading(value.toString());
      const suggestionsLength = suggestionsTuple?.suggestions.length ?? 0;
      const hasOptionsPopup =
        shouldRenderLocationSuggestions(value.toString()) &&
        isInputFocused &&
        !suggestionsAreCollapsed &&
        suggestionsLength > 0;
      const hasPopup = showLoading || hasOptionsPopup;
      const activeDescendant = getValidActiveDescendant({
        activeDescendant: renderInputComponentProps['aria-activedescendant'],
        autosuggestId,
        hasOptionsPopup,
        suggestionsLength,
      });
      const onLocationInputKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setSuggestionsAreCollapsed(true);
          setSuggestionsTuple({ input: value.toString(), suggestions: [] });
          abortGoogleMapsAutocomplete();
        }

        onKeyDown?.(event);
      };

      return (
        <StyledInput
          aria-activedescendant={activeDescendant}
          aria-autocomplete="list"
          aria-controls={hasPopup ? listboxId : undefined}
          aria-expanded={hasPopup}
          aria-label={i18n('Location')}
          autoComplete={autoComplete}
          className={inputClassName}
          disabled={inputDisabled}
          onBlur={inputOnBlur}
          onChange={inputOnChange}
          onFocus={onFocus}
          onKeyDown={onLocationInputKeyDown}
          hasSuccess={hasSuccess}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          required={required}
          // oxlint-disable-next-line jsx_a11y/prefer-tag-over-role
          role="combobox"
          title={title}
          value={value}
          onClear={showClearButton ? clearLocation : null}
          gMapsErrored={gMapsErrored}
          showLocationIcon={showLocationIcon}
          styleVariant={styleVariant}
          affixVariant={affixVariant}
          qaId={qaId ?? 'location-input'}
          id={id}
          inputRef={ref}
        />
      );
    },
    [
      autosuggestId,
      abortGoogleMapsAutocomplete,
      i18n,
      clearLocation,
      gMapsErrored,
      getShowLoading,
      hasSuccess,
      isInputFocused,
      listboxId,
      placeholder,
      required,
      qaId,
      suggestionsAreCollapsed,
      suggestionsTuple,
      affixVariant,
      styleVariant,
      showClearButton,
      showLocationIcon,
      id,
    ],
  );

  const onKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }, []);

  const getSuggestionValue = useCallback(
    (suggestion: LocationSuggestion) =>
      suggestion.placePrediction?.text.text || '',
    [],
  );

  return (
    <AutosuggestWrapper>
      <Autosuggest
        id={autosuggestId}
        highlightFirstSuggestion={highlightFirstSuggestion}
        suggestions={suggestionsTuple?.suggestions ?? []}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={shouldRenderLocationSuggestions}
        renderInputComponent={renderInputComponent}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        containerProps={{
          'aria-expanded': undefined,
          'aria-haspopup': undefined,
          'aria-owns': undefined,
          role: undefined,
        }}
        inputProps={{
          className,
          disabled,
          onChange: onLocationInputValueChange,
          onBlur,
          onFocus: () => {
            setIsInputFocused(true);
            void loadGoogleMapsLibraries();
          },
          onKeyPress,
          placeholder,
          required,
          value: locationInputValue,
          title: i18n('Location'),
          ref: inputRef,
        }}
      />
    </AutosuggestWrapper>
  );
}
